/** @jsx jsx */
import React, { Fragment, useCallback, type PropsWithChildren, type ReactNode } from 'react';
import { cssMap, jsx } from '@compiled/react';
import { HydrationStatsReporter } from '@jira/hydration-stats-reporting/src/ui/index.tsx';
import { TopNavEntry } from '@jira/navigation-apps__page-container-entries-top-nav-entry/src/TopNavEntry.tsx';
import { HorizontalNavEntry } from '@jira/navigation-apps__page-container-entries-horizontal-nav-entry/src/HorizontalNavEntry.tsx';
import { Tokens } from '@jira/platform__project-theme-tokens/src/constants.tsx';
import { isStableUIEnabledForTests } from '@jira/platform__is-stable-ui-enabled-for-tests/src/isStableUIEnabledForTests.tsx';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { useJsmGracePeriodBanner } from '@jira/servicedesk__jsm-grace-period-banner/src/useJsmGracePeriodBanner.tsx';
import { JSMGracePeriodBannerAsync } from '@jira/servicedesk__jsm-grace-period-banner/src/JSMGracePeriodBannerAsync.tsx';
import { useIsChromeless } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/chromeless/index.tsx';
import { useIsFullscreen } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/fullscreen/index.tsx';
import { LayoutRemoteStateUpdater } from '@jira/platform__controllers-layout/src/controllers/layout-controller/consumers/layout-remote-state-updater/index.tsx';
import { RenderOnClient } from '@jira/ssr__ssr-toolkit/src/render-on-client.tsx';
import { Main } from '@jira/design-system__navigation-system/src/PageLayout.tsx';
import {
	GLOBAL_CONTAINERS_MARK,
	GLOBAL_CONTAINERS_START_MARK,
	GLOBAL_CONTAINERS_END_MARK,
} from '@jira/platform__page-container-constants/src/index.tsx';
import { useProjectTheme } from '@jira/platform__project-theme-page-container/src/controllers/project-theme/index.tsx';
import {
	getSharedLayout,
	sharedLayout,
} from '@jira/platform__layouts-jira-shared-layout/src/sharedLayout.tsx';
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';
import { isSystemNavPath } from '@jira/navigation-apps__sidebar-common/src/utils/get-navigation-path/system/index.tsx';
import { isIssuesNavPath } from '@jira/navigation-apps__sidebar-common/src/utils/get-navigation-path/issues/index.tsx';
import { JiraTopBannerPlacement } from '@jira/post-office__top-banner/src/index.tsx';
import { IntegrationsWhichRequireNavigationRoot } from '@jira/platform__layouts-jira-shared-layout/src/ui/integrations/index.tsx';
import { PreviewPanel } from '@jira/issue__preview-panels/src/PreviewPanel.tsx';
import { createRouterSelector } from '@atlassian/react-resource-router';
import { fg } from '@atlassian/jira-feature-gating';
import { setInitialPageLoadTimingFromPerformanceMarks } from '@atlassian/jira-spa-performance-breakdown/src/utils/performance-marks-tools/index.tsx';
import { ContextualAnalyticsData, SCREEN } from '@atlassian/jira-product-analytics-bridge';
import PerformanceMark from '@atlassian/jira-common-performance/src/set-performance-mark.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { Banner } from '../banner/index.tsx';
import { GlobalComponents } from '../global-components/index.tsx';
import { GlobalContainers } from '../global-containers/main.tsx';
import { RightSidebar } from '../right-sidebar/index.tsx';
import { RedirectEntrypointContainer } from '../redirect/index.tsx';
import { RightAdsPanel } from '../right-ads-panel/RightAdsPanel.tsx';
import { JiraPageLayout } from './JiraPageLayout.tsx';
import { GlobalThemeSetter } from './global-theme-setter/GlobalThemeSetter.tsx';
import { ProjectThemeSetter } from './project-theme-setter/ProjectThemeSetter.tsx';
import type { PageContainerProps } from './types.tsx';
import { PageContainerSuccessTracker } from './success-tracker/PageContainerSuccessTracker.tsx';
import { JiraSpaGlobalBottomRightCorner } from './JiraSpaGlobalBottomRightCorner.tsx';

const SidebarEntry = importCond<
	typeof import('@jira/nav-platform-integration__sidebar/src/NavPlatformSidebar.tsx'),
	typeof import('@jira/navigation-apps__page-container-entries-sidebar-entry/src/SidebarEntry.tsx')
>(
	'navx-global-side-nav',
	'@jira/nav-platform-integration__sidebar/src/NavPlatformSidebar.tsx',
	'@jira/navigation-apps__page-container-entries-sidebar-entry/src/SidebarEntry.tsx',
);

const MaybeUFOSegment = componentWithFG(
	'nin_blindspot_fixes_fy26',
	({ children }: PropsWithChildren<{}>) => (
		<UFOSegment name="page-container-v2-main-wrapper">{children}</UFOSegment>
	),
	Fragment,
);

const mainWrapperStyles = cssMap({
	root: {
		height: '100%',
		display: 'flex',
		flexDirection: 'column',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		' > span[data-ep-placeholder-id="route_entry_point"]': {
			// EntryPointPlaceholder span element will stop the height from being 100%.
			// this makes content height can be set to 100% even if the span is present.
			// https://jplat.jira.atlassian.cloud/browse/BLU-3362
			display: 'contents',
		},
	},
	themed: {
		// When an image background is applied via the background css var the image
		// will be darkened in dark mode and lightened in light mode
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		backgroundBlendMode: Tokens.ELEVATION_SURFACE_BLEND_MODE,
	},
});

/**
 * These test IDs are used in pollinator tests. Do not change lightly.
 */
const ROOT_TEST_ID = 'page-layout.root';
const MAIN_TEST_ID = 'page-layout.main';

const DefaultLayout = ({ children }: PageContainerProps) => {
	const [isChromeless] = useIsChromeless();
	const [isFullscreen] = useIsFullscreen();

	const showBanner =
		!isChromeless &&
		!isFullscreen &&
		((!isStableUIEnabledForTests() && fg('jpx-870-stable-ui-for-criterion')) ||
			!fg('jpx-870-stable-ui-for-criterion'));
	const showTopNav = !isChromeless && !isFullscreen;
	const showSidebar = !isChromeless && !isFullscreen;
	const showHorizontalNav = !isChromeless;
	const showRightSidebar =
		!isChromeless &&
		!isFullscreen &&
		((!isStableUIEnabledForTests() && fg('jpx-870-stable-ui-for-criterion')) ||
			!fg('jpx-870-stable-ui-for-criterion'));
	const showLayoutRemoteStateUpdater = !isChromeless && !isFullscreen;

	return (
		<JiraPageLayout testId={ROOT_TEST_ID}>
			<RenderOnClient>
				<IntegrationsWhichRequireNavigationRoot />
			</RenderOnClient>
			{showBanner && (
				<JSErrorBoundary id="banner" packageName="jiraPageContainerV2" fallback="unmount">
					<Banner />
				</JSErrorBoundary>
			)}
			{showTopNav && (
				<UFOSegment name="nav4.topNav">
					<JSErrorBoundary id="top-nav" packageName="jiraPageContainerV2" fallback="unmount">
						<TopNavEntry />
					</JSErrorBoundary>
				</UFOSegment>
			)}
			{showSidebar && (
				<JSErrorBoundary id="sidebar" packageName="jiraPageContainerV2" fallback="flag">
					<SidebarEntry />
				</JSErrorBoundary>
			)}
			<MainWrapper>
				{showHorizontalNav && <HorizontalNavEntry />}
				{children}
			</MainWrapper>
			{showRightSidebar && (
				<JSErrorBoundary id="right-sidebar" packageName="jiraPageContainerV2" fallback="unmount">
					<RightSidebar />
				</JSErrorBoundary>
			)}
			{fg('ads-panel-system-jira-admin') && (
				<JSErrorBoundary id="dst-panel" packageName="jiraPageContainerV2" fallback="unmount">
					<RightAdsPanel />
				</JSErrorBoundary>
			)}
			{fg('jira-glance-panel') && (
				<JSErrorBoundary fallback="unmount" id="preview-panel" packageName="jiraPageContainerV2">
					<PreviewPanel />
				</JSErrorBoundary>
			)}
			{showLayoutRemoteStateUpdater && (
				<JSErrorBoundary
					packageName="jiraPageContainerV2"
					id="layout-remote-state-updater"
					fallback="unmount"
				>
					<LayoutRemoteStateUpdater />
				</JSErrorBoundary>
			)}
		</JiraPageLayout>
	);
};

type MainWrapperProps = {
	children: ReactNode;
};

function MainWrapper(props: MainWrapperProps) {
	const { hasTheming } = useProjectTheme();
	const { isEnabled: isJsmGracePeriodBannerEnabled } = useJsmGracePeriodBanner();

	const currentRoute = useRouterRoute();
	const isIssueOrSystemAdminPage =
		isSystemNavPath(currentRoute.path) || isIssuesNavPath(currentRoute.path);

	return (
		<Main testId={MAIN_TEST_ID}>
			<MaybeUFOSegment>
				<div
					data-vc="page-container-v2-main-wrapper"
					// eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop
					style={
						hasTheming
							? {
									// must be an inline style due to firefox bug with blob URLs: see https://atlassian.slack.com/archives/C02785CLSTW/p1770156142296829
									// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values
									background: Tokens.ELEVATION_SURFACE,
								}
							: undefined
					}
					css={[mainWrapperStyles.root, hasTheming && mainWrapperStyles.themed]}
				>
					{isJsmGracePeriodBannerEnabled && <JSMGracePeriodBannerAsync />}
					{isIssueOrSystemAdminPage && <JiraTopBannerPlacement />}
					{props.children}
				</div>
			</MaybeUFOSegment>
		</Main>
	);
}

const useRouterRoute = createRouterSelector((state) => state.route);
const Layout = (props: PageContainerProps) => {
	const { LayoutOverrideComponent } = useRouterRoute();
	if (LayoutOverrideComponent !== undefined) {
		return <LayoutOverrideComponent>{props.children}</LayoutOverrideComponent>;
	}
	return <DefaultLayout {...props} />;
};

export const JiraSpaPageContainerOld = (props: PageContainerProps): JSX.Element => {
	const { id, locale, messages, Router, routerLinkComponent } = props;
	const pageContainerPerformanceTiming = useCallback(() => {
		setInitialPageLoadTimingFromPerformanceMarks(
			GLOBAL_CONTAINERS_MARK,
			GLOBAL_CONTAINERS_START_MARK,
			GLOBAL_CONTAINERS_END_MARK,
			true,
			false,
		);
	}, []);
	const layout = fg('jpx-870-stable-ui-for-criterion') ? getSharedLayout() : sharedLayout;

	return (
		<>
			<PerformanceMark metricKey={GLOBAL_CONTAINERS_START_MARK} />
			<GlobalContainers
				id={id}
				locale={locale}
				messages={messages}
				Router={Router}
				routerLinkComponent={routerLinkComponent}
			>
				<RedirectEntrypointContainer />
				<GlobalThemeSetter />
				<PerformanceMark
					metricKey={GLOBAL_CONTAINERS_END_MARK}
					onSet={pageContainerPerformanceTiming}
				/>
				<ContextualAnalyticsData sourceType={SCREEN} sourceName="navigationNext">
					<ProjectThemeSetter />
					<Layout {...props} />
					<GlobalComponents sharedGlobalComponents={layout.globalComponents} />
					{isStableUIEnabledForTests() && fg('jpx-870-stable-ui-for-criterion') ? null : (
						<JiraSpaGlobalBottomRightCorner />
					)}
					<PageContainerSuccessTracker />
					<HydrationStatsReporter />
				</ContextualAnalyticsData>
			</GlobalContainers>
		</>
	);
};
